import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { fadeUp, LOGO_DIAMOND } from "@/lib/site";

const FEATURED = [
  {
    klinika: "Implantološki Centar Smile",
    grad: "Bjelovar",
    hero: "1.000+",
    heroLabel: "generiranih upita",
    stats: [
      "100.000 €+ dodatnog prihoda unutar par mjeseci",
      "Prosječni CPL: 10,3 € po upitu",
    ],
    detail: "/results/smile.webp",
  },
  {
    klinika: "Ordinacija dentalne medicine Nela Sabo Kopić",
    grad: "Zagreb",
    hero: "1,12 €",
    heroLabel: "cijena po upitu",
    stats: [
      "550+ generiranih upita",
      "90% zainteresiranosti među upitima",
      "300+ predbilježbi za HZZO",
    ],
    detail: "/results/nela.webp",
  },
  {
    klinika: "Optika Lapić",
    grad: "Sinj",
    hero: "1,53 €",
    heroLabel: "CPM",
    stats: [
      "640.000 pregleda",
      "uz samo 450 € uloženog budžeta",
    ],
    detail: "/results/optika.webp",
  },
];

const SECONDARY = [
  {
    klinika: "Physio Kolar",
    grad: "Pregrada",
    hero: "4 €",
    heroLabel: "CPL u 1. mjesecu",
    stats: [
      "42 visoko zainteresirana upita",
      "Jača prepoznatljivost brenda među konkurencijom",
    ],
  },
  {
    klinika: "SubliPrint",
    grad: "Tisak majica i personalizirani pokloni",
    hero: "0,35 €",
    heroLabel: "CPM u 1. mjesecu",
    stats: [
      "145.000 posjeta",
      "uz samo 50 € uloženog budžeta",
    ],
  },
];

function ResultCard({ c, large }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`case-${c.klinika.split(" ")[0].toLowerCase()}`}
      className="group flex h-full flex-col justify-between border border-[#222] bg-[#0a0a0a] p-8 transition-colors duration-500 hover:border-[#50C878]/50 md:p-10"
    >
      <div className="flex flex-col gap-2">
        <h3 className="font-display text-lg font-bold uppercase leading-tight tracking-tight text-white md:text-xl">
          {c.klinika}
        </h3>
        <span className="text-sm text-white/40">{c.grad}</span>
      </div>

      <div className="my-8">
        <div
          className={`font-display font-black leading-none tracking-tighter text-[#50C878] ${
            large ? "text-6xl md:text-7xl" : "text-5xl md:text-6xl"
          }`}
        >
          {c.hero}
        </div>
        <span className="mt-2 block font-display text-xs font-bold uppercase tracking-[0.18em] text-white/60">
          {c.heroLabel}
        </span>
      </div>

      <ul className="flex flex-col gap-3">
        {c.stats.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-white/60">
            <img src={LOGO_DIAMOND} alt="" className="diamond-bullet mt-1" />
            <span>{s}</span>
          </li>
        ))}
      </ul>

      {c.detail && (
        <div className="mt-8 border-t border-[#222] pt-6">
          <button
            type="button"
            data-testid={`case-details-toggle-${c.klinika.split(" ")[0].toLowerCase()}`}
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between font-display text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:text-[#50C878]"
          >
            {open ? "Sakrij detalje" : "Pogledaj detalje"}
            <ChevronDown size={16} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                key="detail"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-5 border border-[#222] bg-white p-1">
                  <img
                    src={c.detail}
                    alt={`Rezultati kampanje — ${c.klinika}`}
                    loading="lazy"
                    className="w-full"
                  />
                </div>
                <p className="mt-3 text-xs text-white/35">Snimka iz Meta Ads Managera.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default function CaseStudies() {
  return (
    <section className="relative z-10 bg-[#050505] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-6 flex items-center gap-3">
          <img src={LOGO_DIAMOND} alt="" className="diamond-bullet" />
          <span className="eyebrow">Uspješne priče</span>
        </motion.div>
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-4 max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white md:text-6xl">
          Neki od naših <span className="text-[#50C878]">rezultata</span>
        </motion.h2>
        <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-16 max-w-xl text-base leading-relaxed text-white/55">
          Stvarne brojke iz stvarnih kampanja. Bez uljepšavanja.
        </motion.p>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {FEATURED.map((c, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}>
              <ResultCard c={c} large />
            </motion.div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          {SECONDARY.map((c, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}>
              <ResultCard c={c} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
